import { css, CSSObject } from 'styled-components';

import { CommonTheme } from 'app/shared/theme';
import { ImageForDropzone } from 'app/shared/typings';

import { withButtonStyleCSS } from './withButtonStyleCSS';
import { withLinkStyleCSS } from './withLinkStyleCSS';

export interface WithMultiImageDropzoneProps {
  theme: CommonTheme;
  images: ImageForDropzone[];
  thumbnailAspectRatio: string;
  minHeight: string;
}

export const withMultiImageDropzone = ({
  theme,
  images,
  thumbnailAspectRatio,
  minHeight,
}: WithMultiImageDropzoneProps) => css`
  > div:has(.upload-widget__root) {
    height: fit-content !important;
    min-height: ${minHeight} !important;

    .upload-widget__root {
      position: relative;
      min-height: inherit;
      height: fit-content;
    }

    .upload-widget {
      position: relative;
      height: fit-content;
      min-height: inherit;

      .btn, a {
        ${withLinkStyleCSS({ theme, variant: 'secondary' })};
        line-height: 100% !important;
        text-decoration: underline;
        text-transform: capitalize !important;
        padding: 0px !important;
        margin: 0;
        height: fit-content;
        border: none !important;
        outline: none !important;

        &:hover, &:focus, &:active {
          text-decoration: underline;
        }
      }

      .upload-widget__submitted-file__action {
        min-width: unset !important;
        color: ${theme.colors.messaging.error.dark} !important;

        &:hover, &:focus, &:active {
          color: ${theme.colors.messaging.error.light} !important;
        }
      }

      .btn.btn--primary {
        ${withButtonStyleCSS({ variant: 'primary', theme })};
        text-transform: uppercase !important;
      }

      .btn--upload {
        margin-bottom: 0px;
      }

      label.btn:not(.btn--primary) {
        ${withButtonStyleCSS({ variant: 'secondary', theme })};
        text-transform: uppercase !important;
      }
    }

    .upload-widget__submitted-file {
      margin: 0;
    }

    .upload-widget__submitted-file__action--performed {
      text-transform: capitalize !important;
    }

    .upload-widget__submitted-file__message {
      color: ${theme.colors.messaging.error.dark} !important;
    }

    .progress-icon__container {
      height: 40px;
      width: calc(40px * ${thumbnailAspectRatio});
      display: flex;
      align-items: center;
    }

    .progress-icon__thumbnail {
      background-color: ${theme.colors.primary.black.lightest};
      background-image: none !important;
      height: 40px;
      width: auto;
      aspect-ratio: ${thumbnailAspectRatio};
      border-radius: 4px;
    }

    ${images.reduce((acc: CSSObject, image, index) => {
      const parentSelector = image.originalFilename
        ? `.upload-widget__submitted-file__inner:has(.upload-widget__submitted-file__name[title^="${image.originalFilename}"])`
        : `.upload-widget__submitted-file:nth-of-type(${index + 1})`;

      acc[`${parentSelector} .progress-icon__thumbnail`] = {
        backgroundImage: `url(${image.fileUrl}) !important`,
      };
      return acc;
    }, {})};

    .upload-widget__internal__children {
      gap: 8px;
      position: relative;
      width: 100%;
    }

    .upload-widget__main-screen {
      padding: 0px;
      gap: 16px;
    }

    .upload-widget__main-screen__info {
      padding: 0px;
    }

    .upload-widget__main-screen__file-list {
      padding: 0;
      margin: 0;
    }

    .upload-widget__main-screen__file-list__inner {
      padding: 0px;
      gap: 8px;
    }

    .upload-widget__image-editor {
      padding: 0px;
      gap: 0px;
      position: relative;
    }

    .upload-widget__image-editor__header {
      padding: 0px;
      margin-bottom: 16px;

      .text-secondary {
        ${theme.media.mobile`
          display: block;
          font-size: ${theme.fontSizes.fontSize75.mobile} !important;
          line-height: 150%;
        `}

        ${theme.media.smallDesktop`
          font-size: ${theme.fontSizes.fontSize75.desktop} !important;
        `}
      }
    }

    .upload-widget__image-editor__header--empty-non-modal {
      display: none;
    }

    .upload-widget__internal {
      border-radius: 6px;
      border-width: 1px;
      position: relative;
      background-color: ${theme.colors.primary.white.lighter};
      min-height: inherit;
      height: fit-content;
      top: unset;
      left: unset;
      right: unset;
      bottom: unset;
      padding: 24px 28px;
    }

    .upload-widget__image-editor__image {
      position: relative;
      display: flex;
      flex-grow: unset;
      width: fit-content;
      margin-bottom: 16px;

      img {
        max-height: 120px;
        height: fit-content;
        max-width: unset;
      }
    }

    .upload-widget__image-editor__image-padding {
      position: relative;
      width: 100%;
      max-height: 120px;
      height: fit-content;
      left: unset;
      right: unset;
    }

    .upload-widget__image-cropper__overlay {
      position: absolute;
      left: calc(50% - (120px / 2 * ${thumbnailAspectRatio}));
    }

    .upload-widget__controls {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      align-items: center;
      gap: 16px;
      padding: 0;
    }

    .upload-widget__submitted-file__inner {
      padding: 0px;
      gap: 8px;
      justify-content: space-between;
    }

    .upload-widget__submitted-file__text {
      margin: 0;
      flex: 1 1 0;
    }

    .upload-widget__submitted-file__container {
      margin: 0px;
    }

    .text-secondary {
      ${theme.media.mobile`
        display: none;
      `}

      ${theme.media.tablet`
        display: block;
        font-size: ${theme.fontSizes.fontSize75.mobile};
        line-height: 150%;
      `}

      ${theme.media.smallDesktop`
        font-size: ${theme.fontSizes.fontSize75.desktop};
      `}
    }
  }
`;
